// src/components/payment/PurchaseModal.js
import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useMemo } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import PurchaseComponent from './PurchaseComponent';
import { UserContext } from '../../context/UserContext';
import { ConfigContext } from '../../context/ConfigContext';
import { convertCreditsToTargetCurrency, formatCurrency } from '../../utils/priceCalculator';
import './PurchaseModal.css';

const PurchaseModal = ({ isOpen, onClose, purchaseType, propertyId, inquiryId, onPurchaseSuccess, featuredUntil, reportLanguage }) => {
  const { user } = useContext(UserContext);
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation();
  const lang = useLanguage();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(null);
  const [options, setOptions] = useState([]);
  const [isActionDisabled, setIsActionDisabled] = useState(false);

  // Memoize rates to prevent unnecessary re-renders of useEffect
  const rates = useMemo(() => {
    return {
      usdToIlsRate: config?.usdToIlsRate,
      eurToIlsRate: config?.eurToIlsRate,
      creditPointValueILS: config?.creditPointValueILS,
    };
  }, [config]);

  useEffect(() => {
    if (!isOpen || !config) {
      return;
    }

    const isLoggedIn = user && user.isLoggedIn;
    const userCurrency = user?.preferences?.currency || 'ILS';
    const userCredits = user?.creditPoints || 0;

    const handleAuthRedirect = () => {
      if (propertyId) {
        localStorage.setItem('returnToPropertyId', propertyId);
      }
      // We don't navigate here, the Link component does.
      onClose(); // Close the modal before navigating
    };

    if (purchaseType === 'report') {
      setTitle(t('purchaseModal.report.title'));
      setIsActionDisabled(!isLoggedIn);
      const originalCostInCredits = config.creditsForReport || 1;

      // --- START DISCOUNT LOGIC ---
      const applicableDiscount = user.discounts?.find((d) => d.productType === 'report' && d.isActive);
      let finalCostInCredits = originalCostInCredits;
      let appliedDiscountPercentage = 0;

      if (applicableDiscount) {
        appliedDiscountPercentage = applicableDiscount.discountPercentage;
        finalCostInCredits = Math.round(originalCostInCredits * (1 - appliedDiscountPercentage / 100));
      }
      // --- END DISCOUNT LOGIC ---

      const fullPriceConverted = convertCreditsToTargetCurrency(originalCostInCredits, userCurrency, rates);
      const fullPriceInCurrencyDisplay = formatCurrency(fullPriceConverted, userCurrency);

      let finalPriceDisplay;
      let creditsToUse = 0;
      let creditDiscountValue = 0;

      if (isLoggedIn) {
        creditsToUse = Math.min(userCredits, finalCostInCredits);
        const creditsToPay = finalCostInCredits - creditsToUse;
        const finalPriceConverted = convertCreditsToTargetCurrency(creditsToPay, userCurrency, rates);
        finalPriceDisplay = finalPriceConverted > 0 ? formatCurrency(finalPriceConverted, userCurrency) : t('purchaseModal.free');
        creditDiscountValue = convertCreditsToTargetCurrency(creditsToUse, userCurrency, rates);
        setDescription(
          <Trans
            i18nKey="purchaseModal.report.descriptionLoggedIn"
            values={{ email: user.email }}
            components={{
              1: <Link to={`/${lang}/preferences`} onClick={handleAuthRedirect} />,
              br: <br />,
            }}
          />
        );
      } else {
        finalPriceDisplay = fullPriceInCurrencyDisplay;
        setDescription(
          <Trans
            i18nKey="purchaseModal.report.descriptionLoggedOut"
            components={{
              1: <Link to={`/${lang}/login`} onClick={handleAuthRedirect} />,
              3: <Link to={`/${lang}/subscribe`} onClick={handleAuthRedirect} />,
              br: <br />,
            }}
          />
        );
      }
      setOptions([
        {
          productId: 'report',
          title: t('purchaseModal.report.optionTitle'),
          fullPriceInCredits: originalCostInCredits,
          creditsToUse,
          creditDiscountValue,
          finalPriceDisplay,
          fullPriceInCurrencyDisplay,
          appliedDiscountPercentage,
        },
      ]);
    } else if (purchaseType === 'promotion') {
      const isCurrentlyFeatured = featuredUntil && new Date(featuredUntil) > new Date();
      setTitle(isCurrentlyFeatured ? t('purchaseModal.promotion.titleExtend') : t('purchaseModal.promotion.titlePromote'));
      if (isCurrentlyFeatured) {
        const promotionEndDate = new Date(featuredUntil).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        setDescription(t('purchaseModal.promotion.descriptionExtend', { date: promotionEndDate }));
      } else {
        setDescription(t('purchaseModal.promotion.descriptionPromote'));
      }
      const promotionTiers = [
        { id: 'featured_30_days', credits: config.creditsForFeatured1 || 15, days: config.daysForFeatured1 || 30 },
        { id: 'featured_60_days', credits: config.creditsForFeatured2 || 22, days: config.daysForFeatured2 || 60 },
      ];
      const calculatedOptions = promotionTiers.map((tier) => {
        const originalCostInCredits = tier.credits;

        // --- START DISCOUNT LOGIC ---
        const applicableDiscount = user.discounts?.find((d) => d.productType === 'promotion' && d.isActive);
        let finalCostInCredits = originalCostInCredits;
        let appliedDiscountPercentage = 0;

        if (applicableDiscount) {
          appliedDiscountPercentage = applicableDiscount.discountPercentage;
          finalCostInCredits = Math.round(originalCostInCredits * (1 - appliedDiscountPercentage / 100));
        }
        // --- END DISCOUNT LOGIC ---

        const creditsToUse = Math.min(userCredits, finalCostInCredits);
        const creditsToPay = finalCostInCredits - creditsToUse;
        const finalPriceConverted = convertCreditsToTargetCurrency(creditsToPay, userCurrency, rates);
        const creditDiscountValue = convertCreditsToTargetCurrency(creditsToUse, userCurrency, rates);
        const fullPriceConverted = convertCreditsToTargetCurrency(originalCostInCredits, userCurrency, rates);
        const fullPriceInCurrencyDisplay = formatCurrency(fullPriceConverted, userCurrency);
        return {
          productId: tier.id,
          title: t('purchaseModal.promotion.optionTitle', { days: tier.days }),
          fullPriceInCredits: originalCostInCredits,
          creditsToUse,
          creditDiscountValue,
          finalPriceDisplay: formatCurrency(finalPriceConverted, userCurrency || 'ILS'),
          fullPriceInCurrencyDisplay,
          appliedDiscountPercentage,
        };
      });
      setOptions(calculatedOptions);
      setIsActionDisabled(false);
    } else if (purchaseType === 'inquiry') {
      setTitle(t('purchaseModal.inquiry.title'));
      setIsActionDisabled(!isLoggedIn);
      const originalCostInCredits = config.creditsForInquiry || 5;

      // --- START DISCOUNT LOGIC ---
      const applicableDiscount = user.discounts?.find((d) => d.productType === 'inquiry' && d.isActive);
      let finalCostInCredits = originalCostInCredits;
      let appliedDiscountPercentage = 0;

      if (applicableDiscount) {
        appliedDiscountPercentage = applicableDiscount.discountPercentage;
        finalCostInCredits = Math.round(originalCostInCredits * (1 - appliedDiscountPercentage / 100));
      }
      // --- END DISCOUNT LOGIC ---

      const fullPriceConverted = convertCreditsToTargetCurrency(originalCostInCredits, userCurrency, rates);
      const fullPriceInCurrencyDisplay = formatCurrency(fullPriceConverted, userCurrency);

      let finalPriceDisplay;
      let creditsToUse = 0;
      let creditDiscountValue = 0;

      if (isLoggedIn) {
        creditsToUse = Math.min(userCredits, finalCostInCredits);
        const creditsToPay = finalCostInCredits - creditsToUse;
        const finalPriceConverted = convertCreditsToTargetCurrency(creditsToPay, userCurrency, rates);
        finalPriceDisplay = finalPriceConverted > 0 ? formatCurrency(finalPriceConverted, userCurrency) : t('purchaseModal.free');
        creditDiscountValue = convertCreditsToTargetCurrency(creditsToUse, userCurrency, rates);
        setDescription(<Trans i18nKey="purchaseModal.inquiry.descriptionLoggedIn" components={{ br: <br /> }} />);
      } else {
        // This case is unlikely as the button is for logged-in users, but it's a safe fallback.
        finalPriceDisplay = fullPriceInCurrencyDisplay;
        setDescription(
          <Trans i18nKey="purchaseModal.inquiry.descriptionLoggedOut">
            To unlock this inquiry, please <Link to={`/${lang}/login`}>log in</Link>.
          </Trans>
        );
      }
      setOptions([
        {
          productId: 'inquiry',
          title: t('purchaseModal.inquiry.optionTitle'),
          fullPriceInCredits: originalCostInCredits,
          creditsToUse,
          creditDiscountValue,
          finalPriceDisplay,
          fullPriceInCurrencyDisplay,
          appliedDiscountPercentage,
        },
      ]);
    }
  }, [isOpen, purchaseType, user, config, propertyId, inquiryId, featuredUntil, onClose, t, lang, rates]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="purchase-modal-overlay" onClick={onClose}>
      <div className="purchase-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label={t('purchaseModal.closeAriaLabel')}>
          <i className="fas fa-times"></i>
        </button>
        <h2>{title}</h2>
        {description && <p className="purchase-modal-description">{description}</p>}
        {purchaseType === 'report' && reportLanguage && (
          <p className="report-language-display">
            <Trans
              i18nKey="purchaseModal.reportLanguage"
              values={{ language: t(`language_${reportLanguage}`) }}
              components={{
                1: <Link to={`/${lang}/preferences`} onClick={onClose} />,
              }}
            />
          </p>
        )}
        <div className="purchase-options-container">
          {options.map((option, index) => (
            <div key={index} className="purchase-option">
              <h4>{option.title}</h4>
              <div className="price-breakdown">
                <div className="price-line">
                  <span className="price-label">{t('purchaseModal.fullPriceLabel')}</span>
                  <span className="price-value">{option.fullPriceInCurrencyDisplay}</span>
                </div>
                {option.appliedDiscountPercentage > 0 && (
                  <div className="price-line discount">
                    <span className="price-label">{`Personal Discount (${option.appliedDiscountPercentage}%)`}</span>
                    <span className="price-value">
                      - {formatCurrency(convertCreditsToTargetCurrency(option.fullPriceInCredits * (option.appliedDiscountPercentage / 100), user.preferences?.currency || 'ILS', rates), user.preferences?.currency || 'ILS')}
                    </span>
                  </div>
                )}
                {option.creditsToUse > 0 && (
                  <div className="price-line discount">
                    <span className="price-label">{t('purchaseModal.creditsAppliedLabel', { used: option.creditsToUse, total: user.creditPoints })}</span>
                    <span className="price-value">- {formatCurrency(option.creditDiscountValue, user.preferences?.currency || 'ILS')}</span>
                  </div>
                )}
                <div className="price-line total">
                  <span className="price-label">{t('purchaseModal.youPayLabel')}</span>
                  <span className="price-value">{option.finalPriceDisplay}</span>
                </div>
              </div>
              <PurchaseComponent
                productId={option.productId}
                propertyId={propertyId}
                inquiryId={inquiryId}
                buyNowText={t('purchaseModal.buyNowButton')}
                onPurchaseSuccess={onPurchaseSuccess}
                isDisabled={isActionDisabled}
                onAddToCartSuccess={onClose} // Close modal on successful add to cart
              />
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PurchaseModal;
